import { Box, SpaceBetween, Table } from '@cloudscape-design/components';
import { ConfigurationObject } from '../../services/configs-api';

interface HolidayDetailProps {
  config: ConfigurationObject;
}

const NON_DATE_KEYS = new Set(['timeZone', 'timezone', 'message', 'action', 'startDate', 'endDate', '_parentPath', '_businessUnit']);
const DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isDateKey(key: string): boolean {
  if (NON_DATE_KEYS.has(key)) return false;
  if (key.startsWith('_')) return false;
  return DATE_PATTERN.test(key) || ISO_DATE_PATTERN.test(key);
}

/**
 * Human-readable detail view for Holiday/Closure config type.
 * Shows: Timezone, holiday dates table, closure message, action.
 */
export function HolidayDetail({ config }: HolidayDetailProps) {
  const payload = config.payload ?? {};

  const timezone = (payload.timeZone as string) ?? (payload.timezone as string) ?? '';
  const message = (payload.message as string) ?? '';
  const action = (payload.action as string) ?? '';

  // Extract holiday date entries
  const holidays: { date: string; description: string }[] = [];
  for (const [key, val] of Object.entries(payload)) {
    if (isDateKey(key) && typeof val === 'string') {
      holidays.push({ date: key, description: val });
    }
  }
  holidays.sort((a, b) => a.date.localeCompare(b.date));

  // Format action to plain English
  const actionLabels: Record<string, string> = {
    resume: 'Continue the call as normal',
    disconnect: 'End the call',
    transfer: 'Transfer to external number',
  };
  const actionDisplay = action ? (actionLabels[action] ?? action) : '';

  return (
    <SpaceBetween size="m">
      {timezone && (
        <div>
          <Box variant="awsui-key-label">Timezone</Box>
          <div>{timezone}</div>
        </div>
      )}

      {holidays.length > 0 && (
        <div>
          <Box variant="awsui-key-label">Holiday Dates ({holidays.length})</Box>
          <Table
            variant="embedded"
            columnDefinitions={[
              { id: 'date', header: 'Date', cell: (item) => item.date, width: 140 },
              { id: 'description', header: 'Description', cell: (item) => item.description },
            ]}
            items={holidays}
            wrapLines
          />
        </div>
      )}

      {holidays.length === 0 && (
        <div>
          <Box variant="awsui-key-label">Holiday Dates</Box>
          <Box color="text-status-inactive">No holiday dates configured</Box>
        </div>
      )}

      {message && (
        <div>
          <Box variant="awsui-key-label">Closure Message</Box>
          <Box variant="p" color="text-body-secondary">"{message}"</Box>
        </div>
      )}

      {actionDisplay && (
        <div>
          <Box variant="awsui-key-label">Action on Holiday</Box>
          <div>{actionDisplay}</div>
        </div>
      )}
    </SpaceBetween>
  );
}
